import { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import type { ReactNode } from 'react'

import { ChatScreen } from './ChatScreen'
import { useMaintenance } from './lib/maintenance'
import { MaintenanceBanner } from './maintenance/MaintenanceBanner'
import { MaintenanceScreen } from './maintenance/MaintenanceScreen'
import { ReauthModal } from './auth/ReauthModal'
import { useSession } from './session/SessionProvider'

/**
 * Rotalar ve guard'lar.
 *
 * ## Auth ekranlari LAZY
 *
 * Giris yapmis bir kullanici bu chunk'i HIC indirmiyor - ki normal durum bu.
 * Bundle butcesi (ilk JS <= 180 KB gz) bunu zorluyor.
 *
 * ## Guard'lar tek yerde
 *
 * Uc durum var ve ucu de ayri ele alinmak zorunda:
 *   * `loading` -> hicbir sey gosterme. Bu HATA DEGIL bir durum: karar
 *     bilinmeden once chat gostermek, sonra giris ekranina atlamak "sayfa
 *     kisa sure chat gosteriyor sonra yonlendiriyor" hatasidir ve tam olarak
 *     vanilla arayuzde yasandi.
 *   * `mfa_pending` -> yalnizca `/2fa`. Pre-auth bir oturumla sohbete
 *     girilememeli.
 *   * `authenticated` -> uygulama.
 */

const HomeScreen = lazy(() => import('./HomeScreen').then((m) => ({ default: m.HomeScreen })))
const LoginScreen = lazy(() =>
  import('./auth/LoginScreen').then((m) => ({ default: m.LoginScreen })),
)
const RegisterScreen = lazy(() =>
  import('./auth/RegisterScreen').then((m) => ({ default: m.RegisterScreen })),
)
const VerifyScreen = lazy(() =>
  import('./auth/VerifyScreen').then((m) => ({ default: m.VerifyScreen })),
)
const TwoFactorScreen = lazy(() =>
  import('./auth/TwoFactorScreen').then((m) => ({ default: m.TwoFactorScreen })),
)
const ForgotScreen = lazy(() =>
  import('./auth/ForgotScreen').then((m) => ({ default: m.ForgotScreen })),
)
const ResetScreen = lazy(() =>
  import('./auth/ResetScreen').then((m) => ({ default: m.ResetScreen })),
)
const SecurityScreen = lazy(() =>
  import('./security/SecurityScreen').then((m) => ({ default: m.SecurityScreen })),
)
const SettingsScreen = lazy(() =>
  import('./settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
)
const ApiDocsScreen = lazy(() =>
  import('./settings/ApiDocsScreen').then((m) => ({ default: m.ApiDocsScreen })),
)
// Etkilesimli API referansi LAZY: kullanicilarin cogu hic acmiyor.
const ApiReferenceScreen = lazy(() =>
  import('./settings/apiReference/ApiReferenceScreen').then((m) => ({
    default: m.ApiReferenceScreen,
  })),
)
// Paylasilan gorunum LAZY: giris yapmis bir kullanici bu chunk'i genellikle
// HIC indirmiyor, ve ziyaretci de yalnizca onu indiriyor (sohbet ekranini
// degil).
const SharedScreen = lazy(() =>
  import('./share/SharedScreen').then((m) => ({ default: m.SharedScreen })),
)
// Yonetim ekrani LAZY ve kendi CSS chunk'ini tasiyor: kullanicilarin ezici
// cogunlugu yonetici DEGIL ve bu kodu hic indirmiyor.
const AdminScreen = lazy(() =>
  import('./admin/AdminScreen').then((m) => ({ default: m.AdminScreen })),
)

export function AppRoutes() {
  const { status, authenticated, mfaPending, reauthNeeded } = useSession()
  const maintenance = useMaintenance()
  const location = useLocation()

  // Onizleme: yonetici bakim ekranini GERCEK bir bakim olmadan gorebiliyor.
  // Ayri bir yol (`/bakim`) cunku ekranin kendisi tasarim isi ve canliya
  // almadan once gozle dogrulanmasi gerekiyor.
  const previewing = location.pathname === '/bakim'
  if (previewing) {
    const preview = new URLSearchParams(location.search)
    const fake = {
      state: 'active' as const,
      starts_at: new Date().toISOString(),
      expected_end_at: new Date(Date.now() + 55 * 60_000).toISOString(),
      message: preview.get('mesaj'),
    }
    return <MaintenanceScreen info={maintenance.state === 'active' ? maintenance : fake} preview />
  }

  // Bakim BASLADI: uygulamanin geri kalani zaten calismiyor (modeller
  // bellekten bosaltildi), calisir gibi gostermek kullaniciyi calismayan
  // dugmelere tiklatirdi. Paylasim baglantilari DISARIDA: onlar salt
  // okunur ve modele ihtiyac duymuyor.
  const sharedView = location.pathname.startsWith('/s/')
  if (maintenance.state === 'active' && !sharedView) {
    return <MaintenanceScreen info={maintenance} />
  }

  // Karar bilinmeden HICBIR SEY gosterilmiyor.
  if (status === 'loading') return <Splash />

  return (
    <>
      {maintenance.state === 'warning' && <MaintenanceBanner info={maintenance} />}
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/login" element={<PublicOnly>{<LoginScreen />}</PublicOnly>} />
          <Route path="/register" element={<PublicOnly>{<RegisterScreen />}</PublicOnly>} />
          <Route path="/verify" element={<VerifyScreen />} />
          <Route path="/forgot" element={<PublicOnly>{<ForgotScreen />}</PublicOnly>} />
          <Route path="/reset" element={<PublicOnly>{<ResetScreen />}</PublicOnly>} />

          {/* `/2fa` PublicOnly DEGIL: pre-auth oturumla girilen tek ekran. */}
          <Route
            path="/2fa"
            element={mfaPending ? <TwoFactorScreen /> : <Navigate to="/" replace />}
          />

          {/*
            `/s/:slug` GUARD DISINDA ve bu bilincli: paylasimin butun amaci
            giris gerektirmemesi. Sunucu tarafinda `require_login` acikken
            403 donuyor ve ekran ne gerektigini soyluyor - guard'a koymak o
            secenegi anlamsiz yapardi (her paylasim giris isterdi).
          */}
          <Route path="/s/:slug" element={<SharedScreen />} />

          <Route path="/settings" element={<Private>{<SettingsScreen />}</Private>} />
          <Route path="/settings/security" element={<Private>{<SecurityScreen />}</Private>} />
          <Route path="/settings/api-docs" element={<Private>{<ApiDocsScreen />}</Private>} />
          <Route
            path="/settings/api-reference"
            element={<Private>{<ApiReferenceScreen />}</Private>}
          />

          {/*
            Yonetim. Rol kontrolu `<Private>`de DEGIL, ekranin kendisinde:
            guard'a koymak yetkisiz kullaniciyi sessizce `/login`e atardi ve
            "neden geri atildim" sorusunu cevapsiz birakirdi. Ekran kendi
            reddini ACIKCA gosteriyor. Gercek sinir zaten sunucuda.
          */}
          <Route path="/admin" element={<Private>{<AdminScreen />}</Private>} />
          <Route path="/admin/:tab" element={<Private>{<AdminScreen />}</Private>} />
          {/*
            Konusma id'si URL'de: aktif konusmanin TEK dogruluk kaynagi.
            Boylece derin baglanti, geri/ileri tuslari ve sekme paylasimi
            bedava calisiyor - hepsi bir `useState`te tutulsa calismazdi.
            Ayni bilesen iki yolda: `/` yeni sohbet, `/c/:id` mevcut.
          */}
          <Route path="/c/:conversationId" element={<Private>{<ChatScreen />}</Private>} />
          {/*
            Kok "/" digerlerinden FARKLI: oturumsuz ziyaretci `/login`e
            ATILMIYOR, `HomeScreen` gosteriliyor. Once buydu ve "ana sayfa
            yok, direkt giris ekranina dusuyor" sikayetine yol acti - urunu
            hic gormeden giris formuyla karsilasan bir ziyaretci onu
            terk ediyordu.
          */}
          <Route path="/" element={<Root />} />

          {/* Bilinmeyen yol: koke. `/app/rastgele` bos ekran gostermemeli. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {/*
        Modal rotalarin DISINDA: mevcut gorunumun uzerine aciliyor ve
        gezinme onu yikmiyor.
      */}
      {authenticated && reauthNeeded && <ReauthModal />}
    </>
  )
}

/**
 * Kok yol: girisliyse sohbet, degilse ana sayfa.
 *
 * `Private` DEGIL bilincli: `Private` her zaman `/login`e yonlendiriyor,
 * ama kok yolun oturumsuz karsiligi bir yonlendirme degil, kendi basina
 * bir ekran (`HomeScreen`).
 */
function Root() {
  const { authenticated, mfaPending } = useSession()
  if (mfaPending) return <Navigate to="/2fa" replace />
  return authenticated ? <ChatScreen /> : <HomeScreen />
}

/** Giris gerektiren sayfa. */
function Private({ children }: { children: ReactNode }) {
  const { authenticated, mfaPending } = useSession()
  const location = useLocation()

  if (mfaPending) return <Navigate to="/2fa" replace />
  if (!authenticated) {
    // Nereden gelindigi tasiniyor: giristen sonra kullanici istedigi
    // sayfaya donuyor, koke atilmiyor.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  return <>{children}</>
}

/** Giris yapmis kullanicinin gormemesi gereken sayfa. */
function PublicOnly({ children }: { children: ReactNode }) {
  const { authenticated, mfaPending } = useSession()
  if (mfaPending) return <Navigate to="/2fa" replace />
  if (authenticated) return <Navigate to="/" replace />
  return <>{children}</>
}

function Splash() {
  const { t } = useTranslation()
  return (
    <div className="center" role="status" aria-live="polite">
      {t('common.loading')}
    </div>
  )
}
